import type { QuestionnaireResponse } from "@medplum/fhirtypes";

import {
  readYesNoDisplayFromAnswer,
  yesNoDisplayToLoincCoding,
} from "@/components/ClearableYesNoRadioField";
import {
  CERVICAL_EXAM_ACTIVE_LABOR_LINK_ID,
  CERVICAL_EXAM_CONSISTENCY_LINK_ID,
  CERVICAL_EXAM_DATE_LINK_ID,
  CERVICAL_EXAM_DATE_TIME_LINK_ID,
  CERVICAL_EXAM_DILATION_LINK_ID,
  CERVICAL_EXAM_EFFACEMENT_LINK_ID,
  CERVICAL_EXAM_FETAL_PRESENTATION_LINK_ID,
  CERVICAL_EXAM_LOCATION_LINK_ID,
  CERVICAL_EXAM_NOTES_LINK_ID,
  CERVICAL_EXAM_ROOT_LINK_ID,
  CERVICAL_EXAM_SIGNATURE_INITIALS_LINK_ID,
  CERVICAL_EXAM_STATION_LINK_ID,
  CERVICAL_EXAM_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/cervical-exam/constants/cervical-exam-link-ids";
import type { CervicalExamFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/cervical-exam/types/cervical-exam-form";
import { getEmptyCervicalExamFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/cervical-exam/utils/cervical-exam-form-defaults";
import {
  parseCodingFromNativeSelect,
  readCodingAnswerAsNativeSelectValue,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import {
  findQuestionnaireResponseItem,
  parseQuestionnaireResponseLocalValueDate,
  readQuestionnaireDecimalAnswer,
  readQuestionnaireSignatureInitials,
  readQuestionnaireStringAnswer,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/questionnaire-response-readers";

export function mapQuestionnaireResponseToCervicalExamFormValues(
  questionnaireResponse: QuestionnaireResponse | undefined,
): CervicalExamFlowsheetFormValues {
  // Use empty values so a missing or malformed response leaves date/time blank.
  const emptyValues = getEmptyCervicalExamFlowsheetFormValues();
  const root = findQuestionnaireResponseItem(
    questionnaireResponse?.item,
    CERVICAL_EXAM_ROOT_LINK_ID,
  );
  if (!root?.item) {
    return emptyValues;
  }

  const dateTimeGroup = findQuestionnaireResponseItem(root.item, CERVICAL_EXAM_DATE_TIME_LINK_ID);
  const dateAnswer = findQuestionnaireResponseItem(dateTimeGroup?.item, CERVICAL_EXAM_DATE_LINK_ID)
    ?.answer?.[0]?.valueDate;
  const timeAnswer = findQuestionnaireResponseItem(dateTimeGroup?.item, CERVICAL_EXAM_TIME_LINK_ID)
    ?.answer?.[0]?.valueTime;

  const activeLaborItem = findQuestionnaireResponseItem(
    root.item,
    CERVICAL_EXAM_ACTIVE_LABOR_LINK_ID,
  );

  return {
    ...emptyValues,
    examDate: parseQuestionnaireResponseLocalValueDate(dateAnswer),
    examTime: timeAnswer ?? null,
    dilation: readQuestionnaireDecimalAnswer(
      findQuestionnaireResponseItem(root.item, CERVICAL_EXAM_DILATION_LINK_ID),
    ),
    effacement: readQuestionnaireDecimalAnswer(
      findQuestionnaireResponseItem(root.item, CERVICAL_EXAM_EFFACEMENT_LINK_ID),
    ),
    station: readQuestionnaireDecimalAnswer(
      findQuestionnaireResponseItem(root.item, CERVICAL_EXAM_STATION_LINK_ID),
    ),
    consistency: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, CERVICAL_EXAM_CONSISTENCY_LINK_ID),
    ),
    locationOfCervix: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, CERVICAL_EXAM_LOCATION_LINK_ID),
    ),
    fetalPresentation: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, CERVICAL_EXAM_FETAL_PRESENTATION_LINK_ID),
    ),
    activeLabor: readYesNoDisplayFromAnswer(activeLaborItem?.answer?.[0]),
    notes: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, CERVICAL_EXAM_NOTES_LINK_ID),
    ),
    signatureInitials: readQuestionnaireSignatureInitials(
      findQuestionnaireResponseItem(root.item, CERVICAL_EXAM_SIGNATURE_INITIALS_LINK_ID),
    ),
  };
}

/** Nested form shape expected by `getQuestionnaireResponseFromFormValues` and labor bots. */
export function mapCervicalExamFormValuesToQuestionnaireValues(
  formValues: CervicalExamFlowsheetFormValues,
): Record<string, unknown> {
  return {
    [CERVICAL_EXAM_ROOT_LINK_ID]: {
      [CERVICAL_EXAM_DATE_TIME_LINK_ID]: {
        [CERVICAL_EXAM_DATE_LINK_ID]: formValues.examDate,
        [CERVICAL_EXAM_TIME_LINK_ID]: formValues.examTime,
      },
      [CERVICAL_EXAM_DILATION_LINK_ID]: formValues.dilation,
      [CERVICAL_EXAM_EFFACEMENT_LINK_ID]: formValues.effacement,
      [CERVICAL_EXAM_STATION_LINK_ID]: formValues.station,
      [CERVICAL_EXAM_CONSISTENCY_LINK_ID]: parseCodingFromNativeSelect(formValues.consistency),
      [CERVICAL_EXAM_LOCATION_LINK_ID]: parseCodingFromNativeSelect(formValues.locationOfCervix),
      [CERVICAL_EXAM_FETAL_PRESENTATION_LINK_ID]: parseCodingFromNativeSelect(
        formValues.fetalPresentation,
      ),
      [CERVICAL_EXAM_ACTIVE_LABOR_LINK_ID]: yesNoDisplayToLoincCoding(formValues.activeLabor),
      [CERVICAL_EXAM_NOTES_LINK_ID]: formValues.notes,
      [CERVICAL_EXAM_SIGNATURE_INITIALS_LINK_ID]: formValues.signatureInitials,
    },
  };
}
